import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  Input,
  FormHelperText,
  Checkbox,
  CheckboxGroup,
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td
} from '@chakra-ui/react'
import { Fragment, useEffect, useState } from 'react'

import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import { SearchableSelect } from '../../../components/Select'
import { ApiErrorShape, toFormErrors, useAppToast } from '../../../helpers'
import { CommercialConsultantService } from '../../../services/CommercialConsultant/CommercialConsultantService'
import {
  CommercialConsultant,
  CommercialConsultantInput,
  PanelUser,
  WorkTime
} from '../../../services/CommercialConsultant/types'
import { daysOfWeek } from './constants'

const baseFormErrors: Record<string, string> = {}

interface ICommercialConsultantFormModalProps {
  isOpen: boolean
  onClose: () => void
  consultant: CommercialConsultant | null
}

export default function CommercialConsultantFormModal({
  isOpen,
  onClose,
  consultant
}: ICommercialConsultantFormModalProps) {
  const service = new CommercialConsultantService()
  const toast = useAppToast()
  const [saving, setSaving] = useState(false)
  const [users, setUsers] = useState<PanelUser[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [form, setForm] = useState<CommercialConsultantInput>({
    name: '',
    email: '',
    user_id: null,
    work_times: [],
    is_online_recorded_course: false,
    is_online_live_course: false
  })
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [workTimeErrors, setWorkTimeErrors] = useState<string[]>(Array(7).fill(''))

  useEffect(() => {
    if (!isOpen) return

    setLoadingUsers(true)
    service
      .listUsers()
      .then(setUsers)
      .catch(() => toast.error('Erro ao carregar os usuários do painel'))
      .finally(() => setLoadingUsers(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (consultant) {
      const workTimes: WorkTime[] = []
      consultant.work_times.forEach((workTime) => {
        workTimes[workTime.day_of_week - 1] = {
          day_of_week: +workTime.day_of_week,
          start_time: workTime.start_time || '',
          end_time: workTime.end_time || ''
        }
      })
      setForm({
        name: consultant.name,
        email: consultant.email,
        user_id: consultant.user_id ?? null,
        work_times: workTimes,
        is_online_recorded_course: consultant.is_online_recorded_course || false,
        is_online_live_course: consultant.is_online_live_course || false
      })
      setWorkTimeErrors(Array(7).fill(''))
    } else {
      setForm({
        name: '',
        email: '',
        user_id: null,
        work_times: [],
        is_online_recorded_course: false,
        is_online_live_course: false
      })
      setWorkTimeErrors(Array(7).fill(''))
    }
    setFormErrors({})
  }, [consultant, isOpen])

  // Função para validar horários
  const validateWorkTimes = (work_times: WorkTime[]) => {
    const errors = Array(7).fill('')
    for (let i = 0; i < 7; i++) {
      const wt = work_times[i]
      if (wt) {
        const { start_time, end_time } = wt
        // Regra 2: Se um está preenchido, o outro deve estar também
        if ((start_time && !end_time) || (!start_time && end_time)) {
          errors[i] = 'Preencha os dois horários.'
        } else if (start_time && end_time) {
          // Regra 1: início < fim
          if (start_time >= end_time) {
            errors[i] = 'O início deve ser menor que o fim.'
          }
        }
      }
    }
    setWorkTimeErrors(errors)
    return errors
  }

  // Validação automática ao alterar horários
  useEffect(() => {
    validateWorkTimes(form.work_times)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.work_times])

  const handleSave = async () => {
    setSaving(true)
    try {
      const formData = {
        ...form,
        work_times: form.work_times.filter((w) => w?.start_time && w?.end_time)
      }
      // Regra 3: Não permitir submit se houver erro
      const errors = validateWorkTimes(form.work_times)
      if (errors.some((e) => e)) {
        setSaving(false)
        return
      }
      if (consultant) {
        await service.update(consultant.id, formData)
        toast.success('Consultor atualizado com sucesso')
      } else {
        const created = await service.create(formData)
        // O cupom nasce junto com o consultor (ABRA-258); só o código já em uso impede.
        if (created.coupon_conflict) {
          toast.warning(
            'Consultor cadastrado',
            `O código ${created.coupon_conflict} já está em uso — o cupom não foi criado.`
          )
        } else if (created.coupon) {
          toast.success(
            'Consultor cadastrado',
            `Cupom ${created.coupon.code} criado com 5% de desconto e 3% de comissão.`
          )
        } else {
          toast.success('Consultor cadastrado')
        }
      }
      onClose()
    } catch (err) {
      const { status, data } = (err as ApiErrorShape).response ?? {}
      // 400, não 422: failedValidation() monta um 422, mas lança uma ValidationException e o
      // Handler da API renderiza todas elas como 400, descartando a resposta anexada. Sem isso
      // o erro de validação nunca chegava ao campo — só saía o toast genérico.
      if (status === 400 || status === 422) setFormErrors(toFormErrors(baseFormErrors, data?.errors))
      else toast.error('Erro ao salvar consultor', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={`${consultant ? 'Editar' : 'Cadastrar'} Consultor Comercial`}
      onSubmit={handleSave}
      isLoading={saving}
    >
      <Flex direction="column" gap={3}>
        {!consultant && (
          <Alert status="info" borderRadius="md" fontSize="sm">
            <AlertIcon />
            <Box>
              Um cupom global será criado automaticamente para este consultor, com o código derivado do e-mail, 5% de
              desconto e 3% de comissão (5% em evento esgotado). Ele pode ser editado depois em &ldquo;Cupons de
              Desconto&rdquo;.
            </Box>
          </Alert>
        )}
        <Box>
          <FormField label="Nome" error={formErrors.name}>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={saving} />
          </FormField>
        </Box>
        <Box>
          <FormField label="E-mail" error={formErrors.email}>
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={saving} />
          </FormField>
        </Box>
        <FormField label="Usuário do Painel" error={formErrors.user_id}>
          <SearchableSelect
            placeholder="Nenhum"
            value={form.user_id ? String(form.user_id) : ''}
            onChange={(value) => setForm({ ...form, user_id: value ? Number(value) : null })}
            options={users.map((user) => ({ value: String(user.id), label: `${user.name} (${user.email})` }))}
            entity="usuário"
            isDisabled={saving}
            isLoading={loadingUsers}
            isClearable
          />
          <FormHelperText>
            Conta com a qual este consultor acessa o painel. Combinada com a permissão &quot;Editar Apenas os Seus
            Comprovantes de Vendas&quot;, permite que ele edite somente os comprovantes vinculados a si.
          </FormHelperText>
        </FormField>
        <Box>
          <FormField label="Tipos de Curso Atendidos">
            <CheckboxGroup>
              <Flex gap={4} mb={2}>
                <Checkbox
                  isChecked={form.is_online_live_course}
                  onChange={(e) => setForm({ ...form, is_online_live_course: e.target.checked })}
                  isDisabled={saving}
                >
                  Presencial e Online (ao vivo)
                </Checkbox>
                <Checkbox
                  isChecked={form.is_online_recorded_course}
                  onChange={(e) => setForm({ ...form, is_online_recorded_course: e.target.checked })}
                  isDisabled={saving}
                >
                  Online (gravado)
                </Checkbox>
              </Flex>
            </CheckboxGroup>
          </FormField>
        </Box>
        <Box>
          <FormField label="Horários de Trabalho" error={formErrors.work_times}>
            <TableContainer>
              <Table>
                <Thead>
                  <Tr>
                    <Th textAlign="left">Dia</Th>
                    <Th textAlign="center">Início</Th>
                    <Th textAlign="center">Fim</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {daysOfWeek.map((day, idx) => (
                    <Fragment key={day}>
                      <Tr>
                        <Td>{day}</Td>
                        <Td textAlign="center">
                          <Input
                            type="time"
                            size="sm"
                            value={form.work_times[idx]?.start_time || ''}
                            onChange={(e) => {
                              const newWorkTimes = [...form.work_times]
                              if (!newWorkTimes[idx])
                                newWorkTimes[idx] = { day_of_week: idx + 1, start_time: '', end_time: '' }
                              newWorkTimes[idx].start_time = e.target.value
                              setForm({ ...form, work_times: newWorkTimes })
                            }}
                            disabled={saving}
                            w="100%"
                          />
                        </Td>
                        <Td textAlign="center">
                          <Input
                            type="time"
                            size="sm"
                            value={form.work_times[idx]?.end_time || ''}
                            onChange={(e) => {
                              const newWorkTimes = [...form.work_times]
                              if (!newWorkTimes[idx])
                                newWorkTimes[idx] = { day_of_week: idx + 1, start_time: '', end_time: '' }
                              newWorkTimes[idx].end_time = e.target.value
                              setForm({ ...form, work_times: newWorkTimes })
                            }}
                            disabled={saving}
                            w="100%"
                          />
                        </Td>
                      </Tr>
                      {workTimeErrors[idx] && (
                        <Tr>
                          <Td
                            colSpan={3}
                            p={1}
                            style={{ color: 'red', fontSize: 13, paddingLeft: 8, paddingBottom: 4 }}
                          >
                            {workTimeErrors[idx]}
                          </Td>
                        </Tr>
                      )}
                    </Fragment>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </FormField>
        </Box>
      </Flex>
    </FormModal>
  )
}
